import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import Global from '../../../store/global-selectors';
import { messages } from '../messages';
import style from '../style.module.less';

const ZERO_USD = { value: 0, code: 'USD' };

export const ProductFee = ({
  initialProductPrice = ZERO_USD,
  setProductPrice,
  enableProductPrice = false,
  setEnableProductPrice,
  userHasPermission = false,
}) => {
  const [currencyInputValue, setCurrencyInputValue] = useState(initialProductPrice);
  const [isProductFeeOverLimit, setIsProductFeeOverLimit] = useState(currencyInputValue.value > 9999999.99);
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const toggleEnableProductPrice = () => setEnableProductPrice(!enableProductPrice);

  const onValueChange = useCallback(
    (updatedCurrencyValue) => {
      const numericValue = updatedCurrencyValue ? Number(updatedCurrencyValue) : 0.0;
      const newValue = { value: numericValue, code: currencyCode };
      setCurrencyInputValue(newValue);
      setIsProductFeeOverLimit(numericValue > 9999999.99);
    },
    [currencyCode],
  );

  const onBlur = useCallback(() => {
    const newProductPrice = currencyInputValue.value
      ? { value: Number(currencyInputValue.value), code: currencyCode }
      : { value: 0.0, code: currencyCode };
    setProductPrice(newProductPrice);
  }, [setProductPrice, currencyInputValue, currencyCode]);

  return (
    <>
      <div className={style['fulfillment-rates-product-fee']}>
        <FormattedMessage {...messages.enableProductFee} />
        <div className={style['enable-fulfillment-rates-header']}>
          <FormattedMessage {...messages.enableProductFeeHeader} />
        </div>
        <Checkbox
          checked={enableProductPrice}
          label={<FormattedMessage {...messages.enableProductFeeCheckbox} />}
          onChange={toggleEnableProductPrice}
        />
        <div className={style['leftside-label']}>
          <FormattedMessage {...messages.productFee} />
        </div>
        <div className={style['paired-input']}>
          <CurrencyInput
            className={style['currency-input']}
            currencyCode={currencyCode}
            disabled={!userHasPermission || !enableProductPrice}
            onBlur={onBlur}
            onChange={onValueChange}
            showPlaceHolderForZero
            value={currencyInputValue.value}
          />
          {isProductFeeOverLimit ? (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.productFeeOverLimit} />
            </InlineMessage>
          ) : null}
        </div>
      </div>
    </>
  );
};

ProductFee.propTypes = {
  initialProductPrice: PropTypes.shape({
    value: PropTypes.number,
    code: PropTypes.string,
  }),
  setProductPrice: PropTypes.func.isRequired,
  enableProductPrice: PropTypes.bool,
  setEnableProductPrice: PropTypes.func.isRequired,
  userHasPermission: PropTypes.bool,
};
